import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { sendApiRequest } from '../../utils';
import {
  FiGift,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiX,
  FiSave,
  FiRefreshCw,
  FiCalendar,
  FiCheckCircle,
  FiCamera,
  FiAlertCircle,
  FiTrash2 as FiDeleteImage
} from 'react-icons/fi';
import { FaRegImage } from 'react-icons/fa';

const ShowAllRewards = () => {
  const [allRewards, setAllRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentReward, setCurrentReward] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requiredBookings: '',
    rewardValue: '',
    rewardType: 'gift',
    image: '',
    startDate: '',
    endDate: '',
    status: 'active',
    notes: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredRewards = allRewards.filter(reward => {
    const query = searchQuery.toLowerCase();
    return (
      reward.title.toLowerCase().includes(query) ||
      (reward.requiredBookings && reward.requiredBookings.toString().includes(query))
    );
  });

  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const response = await sendApiRequest('api/rewards', {}, 'GET');
        setAllRewards(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        toast.error('Failed to fetch rewards');
        setAllRewards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, []);

  const handleEdit = (reward) => {
    setCurrentReward(reward);
    setFormData({
      title: reward.title,
      description: reward.description,
      requiredBookings: reward.requiredBookings,
      rewardValue: reward.rewardValue,
      rewardType: reward.rewardType,
      image: reward.image,
      startDate: reward.startDate ? new Date(reward.startDate).toISOString().split('T')[0] : '',
      endDate: reward.endDate ? new Date(reward.endDate).toISOString().split('T')[0] : '',
      status: reward.status,
      notes: reward.notes
    });
    setImagePreview(reward.image);
    setIsModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setImagePreview(URL.createObjectURL(file));
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      document.getElementById('edit-reward-image').files = dataTransfer.files;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this reward?')) {
      try {
        await sendApiRequest(`api/rewards/${id}`, {}, 'DELETE');
        setAllRewards(allRewards.filter(reward => reward._id !== id));
        toast.success('Reward deleted successfully');
      } catch (error) {
        toast.error('Failed to delete reward');
      }
    }
  };

  const handleInputChange = (e) => {
    if (e.target.type === 'checkbox') {
      const { name, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let imageUrl = formData.image;

      const fileInput = document.getElementById('edit-reward-image');
      if (fileInput && fileInput.files && fileInput.files[0]) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", fileInput.files[0]);
        formDataUpload.append("upload_preset", "prayagproperties");

        const cloudinaryResponse = await fetch(
          "https://api.cloudinary.com/v1_1/djmyigser/image/upload",
          {
            method: "POST",
            body: formDataUpload,
          }
        );

        const cloudinaryData = await cloudinaryResponse.json();
        if (!cloudinaryData.secure_url) {
          throw new Error("Failed to upload image to Cloudinary");
        }
        imageUrl = cloudinaryData.secure_url;
      }

      const response = await sendApiRequest(
        `api/rewards/${currentReward._id}`,
        { ...formData, image: imageUrl },
        'PUT'
      );

      setAllRewards(allRewards.map(reward =>
        reward._id === currentReward._id ? response.data : reward
      ));

      toast.success('Reward updated successfully');
      setIsModalOpen(false);
      setImagePreview(null);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update reward');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const stats = {
    total: allRewards.length,
    active: allRewards.filter(p => p.status === 'active').length,
    inactive: allRewards.filter(p => p.status === 'inactive').length
  };

  const inputClasses = 'w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200';

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center'>
        <div className='text-center'>
          <div className='relative'>
            <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto'></div>
            <div className='absolute inset-0 flex items-center justify-center'>
              <FiGift className='w-6 h-6 text-indigo-400 animate-pulse' />
            </div>
          </div>
          <p className='mt-4 text-gray-500 font-medium'>Loading rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10'>
      <div className='container mx-auto px-4 max-w-6xl'>
        <div className='mb-8'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
            <div>
              <div className='flex items-center gap-2 mb-2'>
                <div className='w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full'></div>
                <h1 className='text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent'>
                  All Rewards
                </h1>
              </div>
              <p className='text-gray-500 mt-1 text-sm'>
                Manage your reward schemes
              </p>
            </div>
            <div className='flex items-center gap-2'>
              <button
                onClick={() => window.location.reload()}
                className='p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600'
                title='Refresh'
              >
                <FiRefreshCw className='w-5 h-5' />
              </button>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className='bg-white rounded-2xl p-5 shadow-lg border border-gray-100'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500'>Total Rewards</p>
                <p className='text-3xl font-bold text-gray-800'>{stats.total}</p>
              </div>
              <div className='w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg'>
                <FiGift className='w-7 h-7 text-white' />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className='bg-white rounded-2xl p-5 shadow-lg border border-gray-100'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500'>Active Rewards</p>
                <p className='text-3xl font-bold text-emerald-600'>{stats.active}</p>
              </div>
              <div className='w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg'>
                <FiCheckCircle className='w-7 h-7 text-white' />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className='bg-white rounded-2xl p-5 shadow-lg border border-gray-100'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500'>Inactive</p>
                <p className='text-3xl font-bold text-gray-600'>{stats.inactive}</p>
              </div>
              <div className='w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center shadow-lg'>
                <FiX className='w-7 h-7 text-white' />
              </div>
            </div>
          </motion.div>
        </div>

        <div className='bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden'>
          <div className='p-5'>
            <div className='relative'>
              <FiSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
              <input
                type='text'
                placeholder='Search rewards by title or number of bookings...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none'
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className='absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  <FiX className='w-4 h-4' />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className='bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-gradient-to-r from-indigo-50 to-purple-50'>
                <tr>
                  <th className='px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>Reward</th>
                  <th className='px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>Required Bookings</th>
                  <th className='px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>Type</th>
                  <th className='px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>Duration</th>
                  <th className='px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>Status</th>
                  <th className='px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>Actions</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                <AnimatePresence>
                  {filteredRewards.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan='6' className='px-6 py-16 text-center'>
                        <div className='flex flex-col items-center justify-center'>
                          <div className='w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center'>
                            <FiGift className='w-10 h-10 text-gray-400' />
                          </div>
                          <p className='text-gray-500 font-medium text-lg'>No rewards found</p>
                          <p className='text-sm text-gray-400 mt-1'>Try adjusting your search</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : (
                    filteredRewards.map((reward, index) => (
                      <motion.tr
                        key={reward._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.03 }}
                        className='hover:bg-gray-50 transition-colors'
                      >
                        <td className='px-4 py-4'>
                          <div className='flex items-center gap-3'>
                            {reward.image && (
                              <img
                                src={reward.image}
                                alt={reward.title}
                                className='w-10 h-10 rounded-xl object-cover'
                              />
                            )}
                            {!reward.image && (
                              <div className='w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center'>
                                <FiGift className='w-5 h-5 text-indigo-500' />
                              </div>
                            )}
                            <span className='font-medium text-gray-800'>{reward.title}</span>
                          </div>
                        </td>
                        <td className='px-4 py-4'>
                          <span className='font-semibold text-lg text-indigo-600'>{reward.requiredBookings}</span>
                        </td>
                        <td className='px-4 py-4'>
                          <span className='capitalize text-gray-700'>{reward.rewardType}</span>
                        </td>
                        <td className='px-4 py-4'>
                          <div className='text-sm text-gray-600'>
                            {formatDate(reward.startDate)} - {formatDate(reward.endDate)}
                          </div>
                        </td>
                        <td className='px-4 py-4'>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            reward.status === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {reward.status === 'active' ? <FiCheckCircle className='w-3 h-3' /> : <FiX className='w-3 h-3' />}
                            {reward.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className='px-4 py-4'>
                          <div className='flex justify-end gap-2'>
                            <button
                              onClick={() => handleEdit(reward)}
                              className='p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-600 transition'
                              title='Edit Reward'
                            >
                              <FiEdit className='w-4 h-4' />
                            </button>
                            <button
                              onClick={() => handleDelete(reward._id)}
                              className='p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition'
                              title='Delete Reward'
                            >
                              <FiTrash2 className='w-4 h-4' />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isModalOpen && currentReward && (
          <div className='fixed inset-0 z-50 overflow-y-auto'>
            <div className='fixed inset-0 bg-black/60 backdrop-blur-sm' onClick={() => setIsModalOpen(false)} />
            <div className='flex min-h-screen items-center justify-center p-4'>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className='relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden'
              >
                <div className='bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      <div className='p-2 bg-white/20 rounded-xl'>
                        <FiEdit className='w-5 h-5 text-white' />
                      </div>
                      <div>
                        <h2 className='text-xl font-bold text-white'>Edit Reward</h2>
                        <p className='text-indigo-100 text-sm mt-0.5'>Update reward details</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className='text-white/80 hover:text-white hover:bg-white/20 rounded-xl p-2 transition'
                    >
                      <FiX className='w-5 h-5' />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className='p-6 space-y-5'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
                    <div className='md:col-span-2'>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Reward Title
                      </label>
                      <div className='relative'>
                        <FiGift className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                        <input
                          type='text'
                          name='title'
                          value={formData.title}
                          onChange={handleInputChange}
                          className={`${inputClasses} pl-10`}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Required Bookings
                      </label>
                      <div className='relative'>
                        <FiCalendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                        <input
                          type='number'
                          name='requiredBookings'
                          value={formData.requiredBookings}
                          onChange={handleInputChange}
                          className={`${inputClasses} pl-10`}
                          min='1'
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Reward Type
                      </label>
                      <select
                        name='rewardType'
                        value={formData.rewardType}
                        onChange={handleInputChange}
                        className={`${inputClasses} cursor-pointer`}
                      >
                        <option value='gift'>Gift</option>
                        <option value='trip'>Trip</option>
                        <option value='vehicle'>Vehicle</option>
                        <option value='cash'>Cash</option>
                        <option value='other'>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Reward Value
                      </label>
                      <input
                        type='number'
                        name='rewardValue'
                        value={formData.rewardValue}
                        onChange={handleInputChange}
                        className={`${inputClasses}`}
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Start Date
                      </label>
                      <input
                        type='date'
                        name='startDate'
                        value={formData.startDate}
                        onChange={handleInputChange}
                        className={`${inputClasses}`}
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        End Date
                      </label>
                      <input
                        type='date'
                        name='endDate'
                        value={formData.endDate}
                        onChange={handleInputChange}
                        className={`${inputClasses}`}
                      />
                    </div>
                    {/* Image Upload Field */}
                    <div className='md:col-span-2'>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>Upload Reward Image</label>
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                          isDragging 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : imagePreview 
                              ? 'border-emerald-300 bg-emerald-50/30'
                              : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'
                        }`}
                        onClick={() => document.getElementById('edit-reward-image').click()}
                      >
                        <input
                          id="edit-reward-image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                        
                        {imagePreview ? (
                          <div className="flex flex-col items-center">
                            <div className="relative">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-40 h-40 object-cover rounded-xl shadow-lg border-4 border-white"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setImagePreview(null);
                                  document.getElementById('edit-reward-image').value = '';
                                }}
                                className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition"
                              >
                                <FiDeleteImage className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-sm text-emerald-600 mt-3 flex items-center gap-1">
                              <FiCheckCircle className="w-4 h-4" />
                              Image uploaded successfully
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-3">
                              <FiCamera className="w-6 h-6 text-indigo-500" />
                            </div>
                            <p className="text-gray-600 font-medium">Click or drag to upload</p>
                            <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='md:col-span-2'>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Description
                      </label>
                      <textarea
                        name='description'
                        rows={3}
                        value={formData.description}
                        onChange={handleInputChange}
                        className={`${inputClasses}`}
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Notes
                      </label>
                      <textarea
                        name='notes'
                        rows={2}
                        value={formData.notes}
                        onChange={handleInputChange}
                        className={`${inputClasses}`}
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-semibold text-gray-700 mb-2'>
                        Status
                      </label>
                      <select
                        name='status'
                        value={formData.status}
                        onChange={handleInputChange}
                        className={`${inputClasses} cursor-pointer`}
                      >
                        <option value='active'>✓ Active</option>
                        <option value='inactive'>✗ Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className='pt-4 border-t border-gray-100 flex justify-end gap-3'>
                    <button
                      type='button'
                      onClick={() => setIsModalOpen(false)}
                      className='px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition'
                    >
                      Cancel
                    </button>
                    <button
                      type='submit'
                      disabled={isSubmitting}
                      className='flex items-center gap-2 px-6 py-2 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition disabled:opacity-50'
                    >
                      {isSubmitting ? (
                        <>
                          <FiRefreshCw className='w-4 h-4 animate-spin' />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave className='w-4 h-4' />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShowAllRewards;
